'use client';

import { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Add, ArrowBack, Close, Search } from '@mui/icons-material';

import { get } from '@/lib/fetch';

type ChatRow = { id: string; title: string; updated_at: string };

function formatUpdated(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function isSlackChat(id: string) {
  return id.startsWith('slack:');
}

function SlackMark() {
  return (
    <Box component="svg" viewBox="0 0 24 24" aria-hidden sx={{ width: 15, height: 15, display: 'block', flexShrink: 0 }}>
      <path fill="#E01E5A" d="M6 15a2 2 0 0 1-2 2 2 2 0 0 1-2-2 2 2 0 0 1 2-2h2v2zm1 0a2 2 0 0 1 2-2 2 2 0 0 1 2 2v5a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-5z" />
      <path fill="#36C5F0" d="M9 6a2 2 0 0 1-2-2 2 2 0 0 1 2-2 2 2 0 0 1 2 2v2H9zm0 1a2 2 0 0 1 2 2 2 2 0 0 1-2 2H4a2 2 0 0 1-2-2 2 2 0 0 1 2-2h5z" />
      <path fill="#2EB67D" d="M18 9a2 2 0 0 1 2-2 2 2 0 0 1 2 2 2 2 0 0 1-2 2h-2V9zm-1 0a2 2 0 0 1-2 2 2 2 0 0 1-2-2V4a2 2 0 0 1 2-2 2 2 0 0 1 2 2v5z" />
      <path fill="#ECB22E" d="M15 18a2 2 0 0 1 2 2 2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2h2zm0-1a2 2 0 0 1-2-2 2 2 0 0 1 2-2h5a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-5z" />
    </Box>
  );
}

const headerIconSx = {
  width: 30,
  height: 30,
  color: 'text.secondary',
  '&:hover': { color: 'text.primary', bgcolor: 'rgba(0,0,0,0.05)' },
};

export default function ChatList({
  selectedId,
  refreshKey,
  onSelect,
  onNew,
  onBack,
  onClose,
}: {
  selectedId: string | null;
  refreshKey: number;
  onSelect: (chat: ChatRow) => void;
  onNew: () => void;
  onBack?: () => void;
  onClose?: () => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = debounced ? `?q=${encodeURIComponent(debounced)}` : '';
    get(`/api/chat/conversations${params}`)
      .then((data) => {
        if (!cancelled) setChats(Array.isArray(data?.chats) ? data.chats : []);
      })
      .catch(() => {
        if (!cancelled) setChats([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, refreshKey]);

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', color: 'text.primary' }}>
      <Box
        sx={{
          px: 1.25,
          pt: { xs: 'calc(env(safe-area-inset-top) + 10px)', sm: 1 },
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        {onBack && (
          <IconButton size="small" aria-label="Back to chat" onClick={onBack} sx={headerIconSx}>
            <ArrowBack sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', letterSpacing: '-0.02em', flex: 1, pl: onBack ? 0.25 : 0.75 }}>Chats</Typography>
        <IconButton size="small" aria-label="New chat" onClick={onNew} sx={headerIconSx}>
          <Add sx={{ fontSize: 18 }} />
        </IconButton>
        {onClose && (
          <IconButton size="small" aria-label="Close" onClick={onClose} sx={headerIconSx}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
        <TextField
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search chats"
          size="small"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'action.hover',
              fontSize: '0.84rem',
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'divider' },
              '&.Mui-focused': { bgcolor: 'background.paper' },
              '&.Mui-focused fieldset': { borderColor: 'text.disabled', borderWidth: 1 },
            },
          }}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 0.75, pb: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : chats.length === 0 ? (
          <Typography sx={{ px: 1.25, py: 3, color: 'text.secondary', fontSize: '0.84rem' }}>{debounced ? 'No matching chats' : 'No chats yet'}</Typography>
        ) : (
          chats.map((chat) => {
            const selected = chat.id === selectedId;
            const slack = isSlackChat(chat.id);
            return (
              <Box
                key={chat.id}
                component="button"
                type="button"
                onClick={() => onSelect(chat)}
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  border: 0,
                  cursor: 'pointer',
                  px: 1.25,
                  py: 1,
                  mb: 0.25,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.primary',
                  WebkitTextFillColor: 'currentcolor',
                  bgcolor: selected ? 'action.selected' : 'transparent',
                  borderLeft: selected ? '2px solid' : '2px solid transparent',
                  borderLeftColor: selected ? 'text.primary' : 'transparent',
                  '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
                }}
              >
                <Typography component="span" noWrap title={chat.title} sx={{ flex: 1, minWidth: 0, fontSize: '0.84rem', fontWeight: 500, letterSpacing: '-0.011em', lineHeight: 1.35, color: 'text.primary' }}>
                  {chat.title}
                </Typography>
                {slack && (
                  <Box component="span" title="From Slack" aria-label="From Slack" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                    <SlackMark />
                  </Box>
                )}
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', flexShrink: 0, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  {formatUpdated(chat.updated_at)}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
